import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useWindowWidth from '../hooks/useWindowWidth';
import useTranslation from '../hooks/useTranslation';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { TEST_DURATION_MINUTES } from '../config/testMeta';

// ─── Language-keyed content ───────────────────────────────────────────────────
const CONTENT = {
  en: {
    docTitle: 'Future Capability — What Humans Truly Need to Learn',
    heroEyebrow: 'Future Capability',
    heroBrandLine: 'Learn. Judge. Create the Future.',
    heroH1: "It's not about teaching you what to learn.",
    heroH1Accent: "It's about helping you realize that you never truly mastered it.",
    heroSub: "When artificial intelligence is solving 90% of capability problems, what are the abilities that truly matter for humans?",
    heroCta: 'Start the Test',
    heroCtaMeta: `(~${TEST_DURATION_MINUTES} minutes)`,
    heroSecondary: 'Explore the Theory',

    philosophyEyebrow: 'Why Future Education Must Change',
    philosophyP1: "Artificial intelligence is rapidly taking over a vast range of human capabilities: writing code, analyzing data, generating content, processing information. These abilities that once required years of training are being continuously compressed by systems.",
    philosophyP2: "This points to a reality: many of the skills people are currently learning are losing their value.",
    philosophyP3: "What is truly scarce in the future is no longer knowledge, but rather: seeing problems clearly, identifying change, making judgments, and bearing the consequences.",
    philosophyHighlight: "Future Capability focuses on one thing only: the abilities humans truly need in the age of AI.",

    theoriesEyebrow: 'Future Capability Learning Theories',
    theoriesH2: '8 Frameworks for the Age of AI',
    theoriesViewAll: 'View Full Theories',

    testEntryEyebrow: 'Future Capability Test',
    testEntryH2: 'Discover your capability structure in the age of AI.',
    testEntryDesc: 'A 20-minute assessment that maps your capability structure in the age of AI. Afterward you get your profile across five dimensions. Ranking appears once enough members complete the assessment.',
    testResultScore: 'Capability Profile',
    testResultRank: 'Ranking',
    testEntryBtn: 'Start the Test',
    // DB-01 (2026-09-18). `testResultType: 'Capability Type'` and
    // `testEntryTypeMock: 'Future Leader'` are REMOVED, along with "and your capability
    // type" from testEntryDesc. "Future Leader" is the top band of getProfileArchetype
    // (>=82), i.e. the composite rendered as an identity — exactly what Mr. Chen ruled
    // out on 17 September: "do not preserve the same composite through a renamed level,
    // title, or label". This was live on a PUBLIC page, to signed-out visitors.

    arenaEyebrow: 'Multi-Agent Debate Arena (Core Mechanism)',
    arenaH2: "This is not about debating opinions.\nIt is about approaching better structures.",
    arenaDesc: "Here you will: pose questions, engage in multi-perspective confrontation, observe different Agent reasoning paths, and understand how structures are deconstructed and rebuilt.",
    arenaMotif: "The goal is not to win. The goal is: to see the problem itself clearly.",
    arenaBtn: 'Enter the Arena →',

    futureEyebrow: 'Future Structure Discovery',
    futureH2: "We don't start from \"answers.\" We start from \"structural change.\"",
    futureDesc: "You will participate in: future structure identification, opportunity judgment, and complex systems analysis. The most important question is not \"what will happen in the future,\" but rather:",
    futureDescBold: "what is currently changing the structure.",
    futureBtn: 'Join the Challenge',

    communityEyebrow: 'Global Talent Network',
    communityH2: 'Find people who can understand the future together.',
    communityDesc: "Future Capability is a global capability network connecting: students, entrepreneurs, researchers, and engineers. Not for learning knowledge, but for finding people who can understand the future together.",
    communityBtn: 'Join the Community',

    challengeSystemEyebrow: 'Global Challenge System',
    challengeSystemH2: 'Three Types. Three Stages. Multiple Competitions.',
    challengeTypeLabel: 'Challenge Types',
    challengeStageLabel: 'Challenge Stages',
    competitionLabel: 'Open Competitions',
    viewChallengesBtn: 'View Challenges',

    researchEyebrow: 'Research & Theory',
    researchH2: 'The thinking behind Future Capability.',
    researchDesc: "White papers, theoretical papers, and structural notes on what humans need to learn in an AI-transformed world.",
    readResearchBtn: 'Read Research',

    closingEyebrow: 'The Future Has Already Begun',
    closingH2: "Artificial intelligence will not stop.\nChange will not slow down.",
    closingDesc: "The goal of future capability education is singular: to help humans develop abilities that machines cannot replace.",
    closingPrimaryBtn: 'Start the Test',
    closingSecondaryBtn: 'Join Us',

    // KAN-254. Three of the four original stats were not true of the shipped product:
    // "10K+ Members Globally" (no members), "500+ New Words Submitted" and "50+ Active
    // Challenges" (both describe the challenge module, which FEATURE_FLAGS.CHALLENGE_SUBMISSION
    // hides from the MVP — a visitor cannot reach either surface). This section is ungated, so
    // they shipped while the features they counted did not. That is the exact failure this
    // ticket exists to stop: a discerning paid audience checks the easiest claim first.
    //
    // "8 Learning Theories" STAYS. It is backed by SCREEN 3 on this same page, which lists all
    // eight. LEARNING_THEORY gates the submission module, not the theories.
    //
    // Restore a number here when it becomes true, with the source it comes from.
    stats: [
      { n: '8', l: 'Learning Theories' },
    ],
    challengeTypes: [
      { icon: '🔮', label: 'Future Capability Challenges' },
      { icon: '💡', label: 'Future Opportunity Challenges' },
      { icon: '🧩', label: 'Complex Systems Challenges' },
    ],
    challengeStages: [
      { icon: '🏙️', label: 'City Challenges' },
      { icon: '🌐', label: 'National Challenges' },
      { icon: '🌍', label: 'Global Challenges' },
    ],
    competitions: [
      { icon: '📝', label: 'New Vocabulary Competition' },
      { icon: '📚', label: 'Learning Theory Competition' },
      { icon: '🏆', label: 'Global Future Capability Competition' },
    ],
    theories: [
      { num: 1, title: 'Abundance Era Creativity Learning Theory', desc: 'AI makes creation approach infinity. Humans must imagine new structures.' },
      { num: 2, title: 'Uniquely Human Capability and Mental Model Learning Theory', desc: 'AI surpasses execution. The human core shifts to alignable mental structures.' },
      { num: 3, title: 'Grasping the Essentials Learning Theory', desc: 'Information is infinite. Identify change and grasp what matters.' },
      { num: 4, title: 'Ecosystem Competition Learning Theory', desc: 'Competition escalates from company level to ecosystem level.' },
      { num: 5, title: 'Human-AI Collaboration Learning Theory', desc: 'The most powerful productive unit: Human + AI.' },
      { num: 6, title: 'Structural Recognition Learning Theory', desc: 'The source of opportunity: structural change.' },
      { num: 7, title: 'New Theory Creation Learning Theory', desc: 'Old theories fail. When the world accelerates, theories must be created faster.' },
      { num: 8, title: 'Cross-Dimensional Survival Learning Theory', desc: 'Environments expand across systems, civilizations, and contexts.' },
    ],
  },
  zh: {
    docTitle: '未来能力 — 人类真正需要学习的能力',
    heroEyebrow: '未来能力',
    heroBrandLine: 'Learn. Judge. Create the Future.',
    heroH1: '这不是告诉你该学什么。',
    heroH1Accent: '而是帮你意识到，你从未真正掌握它。',
    heroSub: '当人工智能正在解决90%的能力问题时，对人类来说真正重要的能力是什么？',
    heroCta: '开始测评',
    heroCtaMeta: `（约${TEST_DURATION_MINUTES}分钟）`,
    heroSecondary: '探索理论',

    philosophyEyebrow: '为什么未来的教育必须改变',
    philosophyP1: '人工智能正在迅速接管大量人类能力：编写代码、分析数据、生成内容、处理信息。这些曾经需要多年训练的能力，正在被系统不断压缩。',
    philosophyP2: '这指向一个现实：人们目前正在学习的许多技能正在失去价值。',
    philosophyP3: '未来真正稀缺的不再是知识，而是：清晰地看到问题、识别变化、做出判断并承担后果。',
    philosophyHighlight: '未来能力只专注于一件事：人类在人工智能时代真正需要的能力。',

    theoriesEyebrow: '未来能力学习理论',
    theoriesH2: '人工智能时代的8个框架',
    theoriesViewAll: '查看完整理论',

    testEntryEyebrow: '未来能力测评',
    testEntryH2: '发现你在人工智能时代的能力结构。',
    // DB-01 (2026-09-18): the zh half of the same removal. 能力类型 / 未来领导者 are the
    // composite as an identity and go with the English. A criterion does not stop at a
    // locale boundary — the same point as KAN-478's 能力分数.
    testEntryDesc: '一个约20分钟的测评，帮助你识别自己在人工智能时代的能力结构。测评结束后，你将获得：五个维度的能力画像。排名将在足够多的成员完成测评后出现。',
    testResultScore: '能力画像',
    testResultRank: '排名',
    testEntryBtn: '开始测评',

    arenaEyebrow: '多智能体辩论擂台（核心机制）',
    arenaH2: '这不是关于辩论观点。\n而是关于接近更好的结构。',
    arenaDesc: '在这里你将：提出问题、进行多视角对抗、观察不同Agent的推理路径，并理解结构如何被解构和重建。',
    arenaMotif: '目标不是赢。目标是：清晰地看到问题本身。',
    arenaBtn: '进入擂台 →',

    futureEyebrow: '未来结构发现',
    futureH2: '我们不从"答案"出发，我们从"结构变化"出发。',
    futureDesc: '你将参与：未来结构识别、机会判断和复杂系统分析。最重要的问题不是"未来会发生什么"，而是：',
    futureDescBold: '什么正在改变结构。',
    futureBtn: '参与挑战',

    communityEyebrow: '全球人才网络',
    communityH2: '找到能够一起理解未来的人。',
    communityDesc: '未来能力是一个全球能力网络，连接着：学生、创业者、研究人员和工程师。不是为了学习知识，而是为了找到能够一起理解未来的人。',
    communityBtn: '加入社区',

    challengeSystemEyebrow: '全球挑战系统',
    challengeSystemH2: '三种类型。三个阶段。多项竞赛。',
    challengeTypeLabel: '挑战类型',
    challengeStageLabel: '挑战阶段',
    competitionLabel: '公开竞赛',
    viewChallengesBtn: '查看挑战',

    researchEyebrow: '研究与理论',
    researchH2: '未来能力背后的思考。',
    researchDesc: '白皮书、理论论文，以及关于人类在人工智能转型世界中需要学习什么的结构性说明。',
    readResearchBtn: '阅读研究',

    closingEyebrow: '未来已经开始',
    closingH2: '人工智能不会停止。\n变化不会放慢。',
    closingDesc: '未来能力教育的目标是唯一的：帮助人类发展机器无法替代的能力。',
    closingPrimaryBtn: '开始测评',
    closingSecondaryBtn: '加入我们',

    // Mirrors the EN block above (KAN-254). ZH is retired/gated for launch, but the claim ladder
    // is semantic rather than lexical — leaving the false numbers here would restore them the
    // moment anyone re-enables the locale, which is exactly how a corrected claim comes back.
    stats: [
      { n: '8', l: '学习理论' },
    ],
    challengeTypes: [
      { icon: '🔮', label: '未来能力挑战' },
      { icon: '💡', label: '未来机会挑战' },
      { icon: '🧩', label: '复杂系统挑战' },
    ],
    challengeStages: [
      { icon: '🏙️', label: '城市挑战' },
      { icon: '🌐', label: '全国挑战' },
      { icon: '🌍', label: '全球挑战' },
    ],
    competitions: [
      { icon: '📝', label: '新词汇竞赛' },
      { icon: '📚', label: '学习理论竞赛' },
      { icon: '🏆', label: '全球未来能力竞赛' },
    ],
    theories: [
      { num: 1, title: '丰盛时代创造力学习理论', desc: 'AI使创造接近无限。人类必须想象新的结构。' },
      { num: 2, title: '人类独特能力与心智模型学习理论', desc: 'AI超越执行层面。人类核心转向可对齐的心智结构。' },
      { num: 3, title: '把握本质学习理论', desc: '信息是无限的。识别变化并把握重要的事。' },
      { num: 4, title: '生态系统竞争学习理论', desc: '竞争从公司层面升级到生态系统层面。' },
      { num: 5, title: '人机协作学习理论', desc: '最强大的生产单元：人类 + AI。' },
      { num: 6, title: '结构识别学习理论', desc: '机会的来源：结构变化。' },
      { num: 7, title: '新理论创造学习理论', desc: '旧理论失效。当世界加速时，理论必须被更快地创造。' },
      { num: 8, title: '跨维度生存学习理论', desc: '环境跨越系统、文明和情境不断扩展。' },
    ],
  },
};

const HomePage = () => {
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width < 1024;
  const c = CONTENT[lang] || CONTENT.en;

  useDocumentTitle(c.docTitle);

  useEffect(() => {
    if (!document.getElementById('home-animate-style')) {
      const style = document.createElement('style');
      style.id = 'home-animate-style';
      style.textContent = `
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reveal { opacity: 0; }
        .reveal.visible { animation: fadeUp 0.55s ease forwards; }
      `;
      document.head.appendChild(style);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal').forEach((el) => {
        el.classList.remove('visible');
        observer.observe(el);
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <Layout>
      {/* ── SCREEN 1: Hero ───────────────────────────────────────────────── */}
      <section style={s.heroSection}>
        <div style={s.heroInner}>
          <div style={s.heroEyebrow}>{c.heroEyebrow}</div>
          <h1 style={s.heroH1}>
            {c.heroH1}
            <br />
            <span style={s.heroH1Accent}>{c.heroH1Accent}</span>
          </h1>
          <div style={s.heroBrandLine}>{c.heroBrandLine}</div>
          <p style={s.heroSub}>{c.heroSub}</p>
          <div style={s.heroBtns}>
            <button
              className="btn btn-lg"
              style={s.heroPrimaryBtn}
              onClick={() => navigate('/test')}
            >
              {c.heroCta} <span style={s.btnMeta}>{c.heroCtaMeta}</span>
            </button>
            {/* Learning-Theory entry — hidden from MVP (KAN-209) */}
            {FEATURE_FLAGS.LEARNING_THEORY && (
              <button
                className="btn btn-outline btn-lg"
                style={s.heroSecondaryBtn}
                onClick={() => navigate('/learning-theories')}
              >
                {c.heroSecondary}
              </button>
            )}
          </div>
        </div>
        <div style={s.heroOverlay} aria-hidden="true" />
      </section>

      {/* ── SCREEN 2: Core Philosophy ────────────────────────────────────── */}
      <section style={s.philosophySection}>
        <div className="container-sm reveal">
          <div style={s.sectionEyebrow}>{c.philosophyEyebrow}</div>
          <div style={s.philosophyBody}>
            <p style={s.philosophyPara}>{c.philosophyP1}</p>
            <p style={s.philosophyPara}>
              {lang === 'zh' ? (
                c.philosophyP2
              ) : (
                <>{c.philosophyP2.replace('many of the skills people are currently learning are losing their value.', '')}<strong>many of the skills people are currently learning are losing their value.</strong></>
              )}
            </p>
            <p style={s.philosophyPara}>{c.philosophyP3}</p>
            <p style={{ ...s.philosophyPara, ...s.philosophyHighlight }}>{c.philosophyHighlight}</p>
          </div>
        </div>
      </section>

      {/* ── SCREEN 3: 8 Learning Theories ───────────────────────────────── */}
      <section style={s.theoriesSection}>
        <div className="container">
          <div className="reveal">
            <div style={s.sectionEyebrow}>{c.theoriesEyebrow}</div>
            <h2 style={s.sectionH2}>{c.theoriesH2}</h2>
          </div>
          <div style={s.theoriesGrid}>
            {c.theories.map((theory, i) => (
              <div
                key={theory.num}
                className="reveal"
                style={{ ...s.theoryCard, animationDelay: `${i * 0.05}s` }}
              >
                <div style={s.theoryNum}>{String(theory.num).padStart(2, '0')}</div>
                <div style={s.theoryContent}>
                  <div style={s.theoryTitle}>{theory.title}</div>
                  <div style={s.theoryDesc}>{theory.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Learning-Theory entry — hidden from MVP (KAN-209); the 8 theories
              above stay as static content. */}
          {FEATURE_FLAGS.LEARNING_THEORY && (
            <div style={s.theoriesCTA}>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/learning-theories')}
              >
                {c.theoriesViewAll}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── SCREEN 4: Core Entry Point (Test) ───────────────────────────── */}
      <section style={s.testEntrySection}>
        <div className="container-sm reveal">
          <div style={{ ...s.testEntryCard, gridTemplateColumns: isMobile ? '1fr' : '1fr auto' }}>
            <div style={s.testEntryLeft}>
              <div style={s.testEntryEyebrow}>{c.testEntryEyebrow}</div>
              <h2 style={s.testEntryH2}>{c.testEntryH2}</h2>
              <p style={s.testEntryDesc}>{c.testEntryDesc}</p>
              <div style={s.testResultPreview}>
                <div style={s.testResultItem}>
                  <div style={s.testResultIcon}>📊</div>
                  <div style={s.testResultLabel}>{c.testResultScore}</div>
                </div>
                <div style={s.testResultItem}>
                  <div style={s.testResultIcon}>🏅</div>
                  <div style={s.testResultLabel}>{c.testResultRank}</div>
                </div>
              </div>
            </div>
            <div style={{ ...s.testEntryRight, display: isMobile ? 'none' : 'flex' }}>
              <div style={s.mockLevels}>
                {[
                  ['Judgment', 'proficient'],
                  ['Systems', 'developing'],
                  ['Creative Transfer', 'proficient'],
                  ['Learning Agility', 'advanced'],
                ].map(([dim, lvl]) => (
                  <div key={dim} style={s.mockLevelRow}>
                    <span style={s.mockLevelDim}>{dim}</span>
                    <span style={s.mockLevelWord}>{lvl}</span>
                  </div>
                ))}
              </div>
              {/* DB-01 (2026-09-18). The mock identity label that sat here — "Future Leader",
                  getProfileArchetype's >=82 band — is deleted, for the same reason and by the
                  same precedent as the 84/100 recorded immediately below. Removing the NUMBER
                  and keeping the TITLE derived from it preserved the composite under a label,
                  which is the substitution Mr. Chen named explicitly. The per-dimension levels
                  above are the honest preview and were always the substance of this panel.

                  Found on 2026-09-18 by reading the DEPLOYED bundle after a release, not by any
                  guard. Four reasons every existing check missed it: the copy lives in this
                  page's own inline object rather than i18n/translations.js; the vocabulary is
                  getProfileArchetype's ("Future Leader"), not getCapabilityType's
                  (Pioneer/Builder/…) which is what the guards enumerate; it renders only above
                  the mobile breakpoint; and it is a hardcoded mock, so nothing that traces data
                  flow from a schema can reach it. */}
              {/* KAN-254 P1 #3. The illustrative 84/100 is gone. It was already subordinate to
                  the per-dimension levels above (0.95rem, gray-400), which is why it survived the
                  Decision-4 rebuild — but "make it smaller" was never the standard. The output of
                  the assessment is a capability map, never one number, and the front door is the
                  one place a prospective user forms that expectation. The precedent is already
                  set: the fake "Top 12% Globally" mocks were deleted from this page and
                  TestStartPage rather than shrunk. A composite the schema itself declares
                  display-only and never authoritative should not be invented for a mock. */}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              className="btn btn-primary btn-lg"
              style={{ minWidth: '200px' }}
              onClick={() => navigate('/test')}
            >
              {c.testEntryBtn}
            </button>
          </div>
        </div>
      </section>

      {/* ── SCREEN 5: Multi-Agent Debate Arena ── hidden from MVP (KAN-130) ─ */}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && (
      <section style={s.arenaSection}>
        <div style={s.arenaInner}>
          <div className="reveal" style={{ textAlign: 'center' }}>
            <div style={s.arenaEyebrow}>{c.arenaEyebrow}</div>
            <h2 style={s.arenaH2}>
              {c.arenaH2.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}
            </h2>
            <p style={s.arenaDesc}>{c.arenaDesc}</p>
            <p style={s.arenaMotif}>
              <strong>{c.arenaMotif}</strong>
            </p>
            <button
              className="btn btn-lg"
              style={s.arenaBtn}
              onClick={() => navigate('/challenges')}
            >
              {c.arenaBtn}
            </button>
          </div>
        </div>
      </section>
      )}

      {/* ── SCREEN 6: Future Structure Challenge ── hidden from MVP (KAN-130) */}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && (
      <section style={s.challengeSection}>
        <div className="container-sm reveal">
          <div style={s.sectionEyebrow}>{c.futureEyebrow}</div>
          <h2 style={s.sectionH2}>{c.futureH2}</h2>
          <p style={s.sectionLead}>
            {c.futureDesc}{' '}
            <strong>{c.futureDescBold}</strong>
          </p>
          <div style={{ textAlign: 'center' }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/challenges')}
            >
              {c.futureBtn}
            </button>
          </div>
        </div>
      </section>
      )}

      {/* ── SCREEN 7: Community & Network ───────────────────────────────── */}
      <section style={s.communitySection}>
        <div className="container reveal">
          <div style={{ ...s.communityGrid, gridTemplateColumns: isTablet ? '1fr' : '1fr 1fr' }}>
            <div style={s.communityLeft}>
              <div style={s.sectionEyebrowLight}>{c.communityEyebrow}</div>
              <h2 style={s.communityH2}>{c.communityH2}</h2>
              <p style={s.communityDesc}>{c.communityDesc}</p>
              <button
                className="btn btn-lg"
                style={s.communityBtn}
                onClick={() => navigate('/register')}
              >
                {c.communityBtn}
              </button>
            </div>
            <div style={s.communityRight}>
              <div style={{ ...s.communityStats, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                {c.stats.map((stat) => (
                  <div key={stat.l} style={s.statCard}>
                    <div style={s.statNum}>{stat.n}</div>
                    <div style={s.statLabel}>{stat.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SCREEN 8: Challenge System ── hidden from MVP (KAN-130) ───────── */}
      {FEATURE_FLAGS.CHALLENGE_SUBMISSION && (
      <section style={s.challengeSystemSection}>
        <div className="container reveal">
          <div style={s.sectionEyebrow}>{c.challengeSystemEyebrow}</div>
          <h2 style={s.sectionH2}>{c.challengeSystemH2}</h2>

          <div style={s.challengeSystemGrid}>
            <div style={s.challengeSystemBlock}>
              <div style={s.challengeSystemBlockTitle}>{c.challengeTypeLabel}</div>
              {c.challengeTypes.map((item) => (
                <div key={item.label} style={s.challengeSystemItem}>
                  <span style={s.challengeSystemIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            <div style={s.challengeSystemBlock}>
              <div style={s.challengeSystemBlockTitle}>{c.challengeStageLabel}</div>
              {c.challengeStages.map((item) => (
                <div key={item.label} style={s.challengeSystemItem}>
                  <span style={s.challengeSystemIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            <div style={s.challengeSystemBlock}>
              <div style={s.challengeSystemBlockTitle}>{c.competitionLabel}</div>
              {c.competitions.map((item) => (
                <div key={item.label} style={s.challengeSystemItem}>
                  <span style={s.challengeSystemIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/challenges')}
            >
              {c.viewChallengesBtn}
            </button>
          </div>
        </div>
      </section>
      )}

      {/* ── SCREEN 9: Research & Theory — entry into the Learning-Theory module;
          hidden from MVP (KAN-209) since its only action links there. ── */}
      {FEATURE_FLAGS.LEARNING_THEORY && (
        <section style={s.researchSection}>
          <div className="container-sm reveal">
            <div style={s.sectionEyebrow}>{c.researchEyebrow}</div>
            <h2 style={s.sectionH2}>{c.researchH2}</h2>
            <p style={s.sectionLead}>{c.researchDesc}</p>
            <div style={{ textAlign: 'center' }}>
              <button
                className="btn btn-outline btn-lg"
                onClick={() => navigate('/learning-theories')}
              >
                {c.readResearchBtn}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── FINAL SCREEN: Closing ───────────────────────────────────────── */}
      <section style={s.closingSection}>
        <div style={s.closingInner}>
          <div className="reveal">
            <div style={s.closingEyebrow}>{c.closingEyebrow}</div>
            <h2 style={s.closingH2}>
              {c.closingH2.split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}
            </h2>
            <p style={s.closingDesc}>{c.closingDesc}</p>
            <div style={s.closingBtns}>
              <button
                className="btn btn-lg"
                style={s.closingPrimaryBtn}
                onClick={() => navigate('/test')}
              >
                {c.closingPrimaryBtn}
              </button>
              <button
                className="btn btn-lg"
                style={s.closingSecondaryBtn}
                onClick={() => navigate('/register')}
              >
                {c.closingSecondaryBtn}
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  sectionEyebrow: {
    fontSize: '0.8125rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '3px',
    color: 'var(--primary)',
    marginBottom: '0.75rem',
    textAlign: 'center',
  },
  sectionEyebrowLight: {
    fontSize: '0.8125rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '3px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '0.75rem',
  },
  sectionH2: {
    fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
    fontWeight: '700',
    marginBottom: '1.5rem',
    textAlign: 'center',
    color: 'var(--gray-900)',
    lineHeight: '1.25',
  },
  sectionLead: {
    fontSize: '1.0625rem',
    color: 'var(--gray-600)',
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto 2rem',
    lineHeight: '1.75',
  },

  heroSection: {
    position: 'relative',
    background: 'linear-gradient(160deg, #0a0a14 0%, #0f1629 60%, #160a24 100%)',
    color: 'white',
    minHeight: '92vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6rem 1.5rem 5rem',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(99,102,241,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '760px',
    width: '100%',
    textAlign: 'center',
  },
  heroEyebrow: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '4px',
    color: 'rgba(255,255,255,0.4)',
    marginBottom: '1.5rem',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '100px',
    padding: '0.35rem 1rem',
  },
  heroH1: {
    fontSize: 'clamp(1.875rem, 5vw, 3.25rem)',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '1.5rem',
    color: 'rgba(255,255,255,0.92)',
    letterSpacing: '-0.5px',
  },
  heroH1Accent: { color: 'white', fontWeight: '800' },
  heroBrandLine: {
    fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
    fontWeight: '800',
    color: 'white',
    letterSpacing: '0.5px',
    margin: '0 auto 1.5rem',
    maxWidth: '600px',
  },
  heroSub: {
    fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.7',
    maxWidth: '600px',
    margin: '0 auto 2.5rem',
  },
  heroBtns: { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' },
  heroPrimaryBtn: {
    background: 'white', color: '#0a0a14', fontWeight: '700',
    borderRadius: '100px', padding: '0.875rem 2rem', border: 'none', cursor: 'pointer', fontSize: '1rem',
  },
  heroSecondaryBtn: {
    borderColor: 'rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.8)',
    borderRadius: '100px', padding: '0.875rem 2rem', background: 'transparent', cursor: 'pointer', fontSize: '1rem',
  },
  btnMeta: { opacity: 0.6, fontWeight: '400', fontSize: '0.875rem', marginLeft: '0.25rem' },

  philosophySection: { padding: '6rem 1.5rem', background: 'white' },
  philosophyBody: { maxWidth: '640px', margin: '0 auto' },
  philosophyPara: { fontSize: '1.125rem', color: 'var(--gray-700)', lineHeight: '1.8', marginBottom: '1.25rem' },
  philosophyHighlight: {
    fontSize: '1.1875rem', color: 'var(--primary)', fontWeight: '600',
    borderLeft: '3px solid var(--primary)', paddingLeft: '1rem', marginBottom: 0,
  },

  theoriesSection: { padding: '5rem 1.5rem', background: 'var(--gray-50)' },
  theoriesGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1rem', marginBottom: '2.5rem', marginTop: '2rem',
  },
  theoryCard: {
    background: 'white', borderRadius: '10px', padding: '1.25rem 1.5rem',
    display: 'flex', gap: '1rem', alignItems: 'flex-start',
    boxShadow: '0 1px 6px rgba(0,0,0,0.04)', border: '1px solid var(--gray-100)',
  },
  theoryNum: { fontSize: '1.5rem', fontWeight: '800', color: 'var(--gray-200)', lineHeight: '1', minWidth: '32px', flexShrink: 0 },
  theoryContent: { flex: 1 },
  theoryTitle: { fontSize: '0.9375rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '0.375rem', lineHeight: '1.35' },
  theoryDesc: { fontSize: '0.8125rem', color: 'var(--gray-500)', lineHeight: '1.5' },
  theoriesCTA: { textAlign: 'center' },

  testEntrySection: { padding: '5rem 1.5rem', background: 'white' },
  testEntryCard: {
    display: 'grid', gridTemplateColumns: '1fr auto', gap: '2.5rem', alignItems: 'center',
    background: 'linear-gradient(135deg, #faf5ff 0%, #eff6ff 100%)',
    border: '1px solid #e9d5ff', borderRadius: '16px', padding: '2.5rem',
  },
  testEntryLeft: {},
  testEntryEyebrow: {
    fontSize: '0.8125rem', fontWeight: '700', textTransform: 'uppercase',
    letterSpacing: '3px', color: 'var(--primary)', marginBottom: '0.75rem',
  },
  testEntryH2: { fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontWeight: '700', color: 'var(--gray-900)', lineHeight: '1.3', marginBottom: '1rem' },
  testEntryDesc: { fontSize: '1rem', color: 'var(--gray-600)', lineHeight: '1.7', marginBottom: '1.5rem' },
  testResultPreview: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap' },
  testResultItem: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  testResultIcon: { fontSize: '1.125rem' },
  testResultLabel: { fontSize: '0.8125rem', fontWeight: '600', color: 'var(--gray-700)' },
  testEntryRight: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '0.35rem',
    background: 'white', borderRadius: '12px', padding: '1.5rem 1.75rem',
    boxShadow: '0 4px 24px rgba(99,102,241,0.12)', minWidth: '210px', flexShrink: 0,
  },
  // The six per-dimension levels lead (size, position, weight); the 0–100 is subordinate.
  mockLevels: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' },
  mockLevelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.9rem' },
  mockLevelDim: { fontSize: '0.8125rem', color: 'var(--gray-500)' },
  mockLevelWord: { fontSize: '0.95rem', fontWeight: '700', color: 'var(--primary)', textTransform: 'capitalize' },
  mockType: { fontSize: '0.85rem', fontWeight: '600', color: 'var(--gray-700)', marginTop: '0.35rem' },
  // mockScoreNum / mockScoreMax removed with the 84/100 (KAN-254). Leaving styles for a deleted
  // claim behind is an invitation to put the claim back.

  arenaSection: { background: 'linear-gradient(160deg, #0f1629 0%, #1a0f29 100%)', color: 'white', padding: '7rem 1.5rem' },
  arenaInner: { maxWidth: '680px', margin: '0 auto' },
  arenaEyebrow: {
    fontSize: '0.8125rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '3px',
    color: 'rgba(255,255,255,0.35)', marginBottom: '1.5rem', textAlign: 'center',
  },
  arenaH2: {
    fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontWeight: '800', lineHeight: '1.2',
    color: 'white', marginBottom: '1.25rem', textAlign: 'center',
  },
  arenaDesc: {
    fontSize: '1.0625rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.75',
    textAlign: 'center', marginBottom: '1rem', maxWidth: '580px', marginLeft: 'auto', marginRight: 'auto',
  },
  arenaMotif: {
    fontSize: '1.0625rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center',
    marginBottom: '2.5rem', fontStyle: 'italic',
  },
  arenaBtn: {
    display: 'block', margin: '0 auto', background: 'white', color: '#0f1629',
    fontWeight: '700', borderRadius: '100px', padding: '0.875rem 2.5rem',
    border: 'none', cursor: 'pointer', fontSize: '1rem',
  },

  challengeSection: { padding: '6rem 1.5rem', background: 'var(--gray-50)' },

  communitySection: { background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', padding: '5rem 1.5rem', color: 'white' },
  communityGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' },
  communityLeft: {},
  communityH2: { fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', fontWeight: '700', color: 'white', lineHeight: '1.3', marginBottom: '1rem' },
  communityDesc: { fontSize: '1rem', color: 'rgba(255,255,255,0.75)', lineHeight: '1.75', marginBottom: '1.75rem' },
  communityBtn: {
    background: 'white', color: '#4F46E5', fontWeight: '700', borderRadius: '100px',
    padding: '0.875rem 2rem', border: 'none', cursor: 'pointer', fontSize: '1rem',
  },
  communityRight: {},
  communityStats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' },
  statCard: {
    background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '1.25rem',
    textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
  },
  statNum: { fontSize: '2rem', fontWeight: '800', color: 'white', lineHeight: '1', marginBottom: '0.375rem' },
  statLabel: { fontSize: '0.8125rem', color: 'rgba(255,255,255,0.65)', fontWeight: '500' },

  challengeSystemSection: { padding: '5rem 1.5rem', background: 'white' },
  challengeSystemGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginTop: '2rem' },
  challengeSystemBlock: { background: 'var(--gray-50)', borderRadius: '12px', padding: '1.5rem', border: '1px solid var(--gray-100)' },
  challengeSystemBlockTitle: {
    fontSize: '0.8125rem', fontWeight: '700', textTransform: 'uppercase',
    letterSpacing: '2px', color: 'var(--primary)', marginBottom: '1rem',
  },
  challengeSystemItem: {
    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0',
    borderBottom: '1px solid var(--gray-200)', fontSize: '0.9375rem', color: 'var(--gray-800)', fontWeight: '500',
  },
  challengeSystemIcon: { fontSize: '1.25rem', flexShrink: 0 },

  researchSection: { padding: '6rem 1.5rem', background: 'var(--gray-50)' },

  closingSection: {
    background: 'linear-gradient(160deg, #060610 0%, #0f0a1f 100%)',
    color: 'white', padding: '8rem 1.5rem 7rem', textAlign: 'center',
  },
  closingInner: { maxWidth: '600px', margin: '0 auto' },
  closingEyebrow: {
    fontSize: '0.8125rem', fontWeight: '700', textTransform: 'uppercase',
    letterSpacing: '4px', color: 'rgba(255,255,255,0.3)', marginBottom: '1.5rem',
  },
  closingH2: {
    fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: '800', lineHeight: '1.2',
    color: 'white', marginBottom: '1.25rem', letterSpacing: '-0.5px',
  },
  closingDesc: { fontSize: '1.125rem', color: 'rgba(255,255,255,0.55)', lineHeight: '1.7', marginBottom: '3rem' },
  closingBtns: { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' },
  closingPrimaryBtn: {
    background: 'white', color: '#060610', fontWeight: '700', borderRadius: '100px',
    padding: '0.875rem 2.5rem', border: 'none', cursor: 'pointer', fontSize: '1rem',
  },
  closingSecondaryBtn: {
    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', fontWeight: '600',
    borderRadius: '100px', padding: '0.875rem 2.5rem', border: '1px solid rgba(255,255,255,0.2)',
    cursor: 'pointer', fontSize: '1rem',
  },
};

export default HomePage;
